import _ from "lodash";
import React from "react";
import classNames from "classnames";
import moment from "moment";
import {
  AnalyticsOutlined,
  ArrowOutOutlined,
  CloseCircleOutlined,
} from "@toddle-edu/ds-icons";
import {
  Avatar,
  Badge,
  Checkbox,
  IconButton,
  Tooltip,
} from "@toddle-edu/ds-web";

import ACLStore from "lib/aclStore";
import { useI18n } from "Hooks";
import { POISvg } from "SvgComponents";
import { getIconComponent } from "Administrator/modules/Utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { getLabelWithArchivedText, onEnterOrSpaceKeyDown } from "Utils";
import AssessmentIcon from "UIComponents/AssessmentIcon";
import { ENABLE_STANDARDS_V2 } from "Constants/permissionConstants";
import { getFormattedUserName } from "Utils/GetFormattedUserName";

import classes from "./ResourceCard.scss";

const BADGE_OFFSET = [2, 4];

const ResourceCard = props => {
  const {
    isSelected,
    onClickSelection,
    onClickMedia,
    item,
    type,
    isDisabled,
    isNew,
    isArchived = false,
    showDeleteOption = false,
    onClickDelete,
    testId,
  } = props;

  const { t } = useI18n(["common"]);

  const handleMediaClick = e => {
    e?.stopPropagation();
    onClickMedia({ data: item, evidenceType: item.itemType });
  };

  const handleDelete = e => {
    e?.stopPropagation();
    if (onClickDelete) onClickDelete({ data: item, isSelected });
  };

  const handleStopPropagation = e => {
    e?.stopPropagation();
  };

  const onKeyDownSelection = e => {
    e?.stopPropagation();
    onEnterOrSpaceKeyDown(e, onClickSelection);
  };

  const containerClasses = classNames(classes.container, {
    [classes.containerDisable]: isDisabled,
    [classes.containerSelected]: isSelected,
    [classes.containerArchived]: isArchived,
  });
  const titleClasses = classNames("text-label", classes.title);
  const subTitleClasses = classNames("text-body-s", classes.subTitle);

  const getSnsLabel = () => {
    const isStandardsV2 = ACLStore.can(ENABLE_STANDARDS_V2);
    return _.get(
      item,
      isStandardsV2 ? "plannerElementNodeSet.title" : "name",
      t("common:untitled")
    );
  };
  const getTitle = () => {
    switch (type) {
      case "CLASSROOM":
      case "LEARNING_EXPERIENCES":
        return item.title?.value;
      case "progressReportSelection": {
        const student = _.get(item, "student", {}) || {};
        return getFormattedUserName({ user: student });
      }
      case "sns": {
        return getLabelWithArchivedText({
          label: getSnsLabel(),
          isArchived,
          t,
        });
      }
      case "planning_insights":
        return _.get(item, "label");
      case "poi":
        return _.get(item, "label", "");
    }
  };

  const getSubTitle = () => {
    switch (type) {
      case "CLASSROOM":
        return moment(item.date).format("ll");
      case "LEARNING_EXPERIENCES":
        return moment(item.createdAt).format("ll");
      case "progressReportSelection": {
        const progressReportTitle = _.get(
          item,
          "courseProgressReportTask.title"
        );
        const date = moment(item.createdAt).format("MMMM YYYY");
        return `${progressReportTitle} - ${date}`;
      }
      case "planning_insights":
        return _.get(item, "academicYear");
      case "poi":
        return t("planningInsights:programme_of_inquiry");
    }
  };

  const getIcon = () => {
    switch (type) {
      case "CLASSROOM": {
        const assessmentType = item.type;
        const image = _.get(item, "image.value");

        return (
          <AssessmentIcon
            src={image}
            assessmentType={assessmentType}
            className={classes.avatar}
          />
        );
      }
      case "LEARNING_EXPERIENCES": {
        const assessmentType = _.get(item, "assessmentType.value", "");
        const image = _.get(item, "image.value");

        return (
          <AssessmentIcon
            src={image}
            assessmentType={assessmentType}
            className={classes.avatar}
          />
        );
      }
      case "progressReportSelection": {
        const student = _.get(item, "student", {}) || {};

        const { id, profileImage } = student;
        return (
          <Avatar
            dsVersion="2.0"
            className={classes.avatar}
            shape="square"
            size="medium"
            name={getFormattedUserName({ user: student })}
            id={id}
            src={profileImage}
          />
        );
      }
      case "sns": {
        const { icon } = item;
        return getIconComponent({
          icon,
          size: "x-small",
          hasBackground: true,
        });
      }
      case "planning_insights": {
        const { itemType } = item;
        return (
          <Avatar
            dsVersion="2.0"
            className={classes.avatar}
            shape="square"
            size="medium"
            iconType="icon"
            id={itemType}
            icon={<AnalyticsOutlined />}
          />
        );
      }
      case "poi": {
        const { index } = item;
        const POI_CLASSES = [
          classes.pinkIcon,
          classes.blueIcon,
          classes.yellowIcon,
        ];
        return (
          <div className={POI_CLASSES[index % 3]}>
            <POISvg width={"100%"} height={"100%"} />
          </div>
        );
      }
    }
  };

  const getCardLabel = () => {
    if (type === "sns") {
      return getSnsLabel();
    } else {
      return getTitle();
    }
  };

  const title = getTitle();
  const subtitle = getSubTitle();
  const updatedTitle = !_.isEmpty(title) ? title : t("common:untitled");
  const cardLabel = getCardLabel();

  return (
    <ConditionalWrapper
      type="wrapperAsElement"
      condition={isNew}
      wrapper={
        <Badge
          type="dot"
          dsVersion="2.0"
          offset={BADGE_OFFSET}
          size="xxx-small"
        />
      }
    >
      <div
        onKeyDown={onKeyDownSelection}
        className={containerClasses}
        onClick={onClickSelection}
        data-test-id={testId}
      >
        {getIcon()}
        <div className={classes.details}>
          <Tooltip dsVersion="2.0" tooltip={updatedTitle}>
            <div dir="auto" className={titleClasses}>
              {updatedTitle}
            </div>
          </Tooltip>
          <Tooltip dsVersion="2.0" tooltip={subtitle}>
            <div dir="auto" className={subTitleClasses}>
              {subtitle}
            </div>
          </Tooltip>
        </div>
        <div className={classes.actions}>
          <div className={classes.overlay}>
            <IconButton
              dsVersion="2.0"
              variant="neutral"
              type="plain"
              icon={<ArrowOutOutlined variant="subtle" />}
              onClick={handleMediaClick}
              aria-label={t("common:open_label_in_new_tab", {
                label: cardLabel,
              })}
              onKeyDown={handleStopPropagation}
            />

            {showDeleteOption ? (
              <IconButton
                icon={<CloseCircleOutlined variant="subtle" />}
                dsVersion="2.0"
                variant="neutral"
                type="plain"
                onClick={handleDelete}
                aria-label={t("common:delete_label", {
                  label: cardLabel,
                })}
                onKeyDown={handleStopPropagation}
              />
            ) : null}
          </div>
          <Checkbox
            dsVersion="2.0"
            isChecked={isSelected}
            aria-label={cardLabel}
          />
        </div>
      </div>
    </ConditionalWrapper>
  );
};

export default ResourceCard;
